import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, RefreshCw, Video, MapPin, ArrowRight, Clock } from "lucide-react";
import MarketplaceNav from "@/components/coachly/MarketplaceNav";
import { bookingsApi } from "@/lib/bookingsApi";
import { useAuth } from "@/context/AuthContext";
import { formatInTz, localTz, fmtMoney, shortModeLabel } from "@/lib/sessionFormat";

const CLIENT_TZ = localTz();

// MySessions — private page. Lists every booking that belongs to the signed-in
// user. "Book again" is surfaced on completed sessions so a repeat purchase
// is one tap → the same coach's booking widget with email/name pre-filled.
export default function MySessions() {
  const { user, openSignIn, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    bookingsApi.mine()
      .then((r) => { if (!cancelled) setBookings(r.bookings || []); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "Couldn't load your sessions.");
        setBookings([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.email]);

  const groups = useMemo(() => {
    const now = Date.now();
    const upcoming = [];
    const completed = [];
    const other = [];
    for (const b of bookings) {
      const t = new Date(b.session_at).getTime();
      if (b.status === "completed") completed.push(b);
      else if (b.status === "paid" && t > now) upcoming.push(b);
      else other.push(b);
    }
    // Upcoming sorted asc (soonest first), completed sorted desc (recent first).
    upcoming.sort((a, b) => new Date(a.session_at) - new Date(b.session_at));
    completed.sort((a, b) => new Date(b.session_at) - new Date(a.session_at));
    return { upcoming, completed, other };
  }, [bookings]);

  return (
    <div data-testid="my-sessions-page" className="bg-white min-h-screen">
      <MarketplaceNav />

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-8">
        <div className="max-w-3xl">
          <div className="eyebrow inline-flex items-center gap-2">
            <CalendarCheck className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={2.4} />
            § My sessions
          </div>
          <h1 className="mt-4 font-display font-medium text-[44px] sm:text-[64px] leading-[0.98] tracking-[-0.05em] text-slate-900">
            Your <span className="italic font-light text-[#2563EB]">sessions.</span>
          </h1>
          <p className="mt-4 text-slate-600 text-lg max-w-[54ch] leading-relaxed">
            What's booked, what's done, and one-tap rebooking with the coaches you loved.
          </p>
        </div>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-24 space-y-12">
        {authLoading ? (
          <div className="text-[13px] text-slate-400">Loading…</div>
        ) : !user ? (
          <SignInGate onSignIn={openSignIn} />
        ) : loading ? (
          <SkeletonList />
        ) : error ? (
          <div data-testid="my-sessions-error" className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-[13.5px] text-rose-700">
            {error}
          </div>
        ) : bookings.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <Group
              title="Upcoming"
              testId="upcoming"
              empty="Nothing on the books right now."
              bookings={groups.upcoming}
              renderCta={(b) => <StatusPill status="Confirmed" tone="blue" />}
            />
            <Group
              title="Completed"
              testId="completed"
              empty="No completed sessions yet."
              bookings={groups.completed}
              renderCta={(b) => (
                b.rebookable ? (
                  <Link
                    to={`/coach/${b.coach_slug}?rebook=1#booking-widget`}
                    data-testid={`book-again-${b.id}`}
                    className="btn-primary h-10 px-4 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5"
                  >
                    <RefreshCw className="w-3.5 h-3.5" strokeWidth={2.4} />
                    Book again
                  </Link>
                ) : (
                  <span data-testid={`rebook-unavailable-${b.id}`} className="text-[12px] text-slate-400">Coach unavailable</span>
                )
              )}
            />
            {groups.other.length > 0 && (
              <Group
                title="Other"
                testId="other"
                empty=""
                bookings={groups.other}
                renderCta={(b) => <StatusPill status={b.status} tone="slate" />}
              />
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Group({ title, testId, bookings, empty, renderCta }) {
  if (!bookings || bookings.length === 0) {
    if (!empty) return null;
    return (
      <div data-testid={`my-sessions-group-${testId}`}>
        <h2 className="font-display text-2xl tracking-tight text-slate-900">{title}</h2>
        <div className="mt-3 rounded-2xl border border-dashed border-slate-200 p-6 text-[13.5px] text-slate-500">
          {empty}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={`my-sessions-group-${testId}`}>
      <h2 className="font-display text-2xl tracking-tight text-slate-900">
        {title} <span className="text-slate-400 text-[15px] font-normal">({bookings.length})</span>
      </h2>
      <ul className="mt-4 space-y-3">
        {bookings.map((b) => (
          <SessionRow key={b.id} booking={b} cta={renderCta(b)} />
        ))}
      </ul>
    </div>
  );
}

function SessionRow({ booking: b, cta }) {
  const isOnline = b.mode === "online";
  const initial = (b.coach_name || "?").trim().slice(0, 1).toUpperCase();
  return (
    <li
      data-testid={`session-row-${b.id}`}
      className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4"
    >
      <Link
        to={`/coach/${b.coach_slug}`}
        className="flex items-center gap-3 md:w-[36%] min-w-0"
      >
        {b.coach_photo ? (
          <img
            src={b.coach_photo}
            alt=""
            className="w-12 h-12 rounded-full object-cover shrink-0"
            loading="lazy"
          />
        ) : (
          <span className="w-12 h-12 rounded-full bg-slate-900 text-white grid place-items-center text-[15px] font-semibold shrink-0">
            {initial}
          </span>
        )}
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 truncate">{b.coach_name}</div>
          <div className="text-[12.5px] text-slate-500 truncate">
            {(b.coach_specialties || []).slice(0, 2).join(" · ") || "Coach"}
          </div>
        </div>
      </Link>

      <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-3 gap-3 text-[13px] text-slate-600">
        <div className="inline-flex items-center gap-1.5 min-w-0">
          <CalendarCheck className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.2} />
          <span className="truncate">{formatInTz(b.session_at, CLIENT_TZ, { weekday: "short" })}</span>
        </div>
        <div className="inline-flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.2} />
          <span>{b.duration_minutes}m</span>
        </div>
        <div className="inline-flex items-center gap-1.5 min-w-0">
          {isOnline ? <Video className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.2} />
                    : <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" strokeWidth={2.2} />}
          <span className="truncate">{shortModeLabel(b.mode)}</span>
        </div>
      </div>

      <div className="flex items-center justify-between md:justify-end gap-3 md:w-auto">
        <div className="text-[13px] text-slate-500 md:text-right">
          <div className="font-semibold text-slate-900">{fmtMoney(b.price_cents, (b.currency || "usd").toUpperCase())}</div>
        </div>
        {cta}
      </div>
    </li>
  );
}

function StatusPill({ status, tone }) {
  const toneCls = tone === "blue"
    ? "bg-[#2563EB]/10 text-[#1D4ED8] border-[#2563EB]/20"
    : "bg-slate-100 text-slate-600 border-slate-200";
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`h-8 px-3 rounded-full border text-[11.5px] font-semibold inline-flex items-center ${toneCls}`}>
      {label}
    </span>
  );
}

function SignInGate({ onSignIn }) {
  return (
    <div
      data-testid="my-sessions-signin-required"
      className="max-w-md rounded-2xl border border-slate-200 p-6"
    >
      <div className="eyebrow">§ Sign in</div>
      <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">
        Your booking history is tied to your Google account so only you can see it.
      </p>
      <button
        type="button"
        data-testid="my-sessions-signin"
        onClick={() => onSignIn("Sign in to see your sessions.")}
        className="mt-4 h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors"
      >
        Continue with Google →
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="my-sessions-empty" className="text-center py-16 max-w-lg mx-auto">
      <div className="eyebrow">§ No sessions yet</div>
      <h3 className="mt-4 font-display text-3xl tracking-tight text-slate-900">
        Ready for your first session?
      </h3>
      <p className="mt-2 text-slate-500">
        Browse coaches, pick a time, and check out in under a minute.
      </p>
      <Link
        to="/marketplace"
        className="mt-6 inline-flex h-11 px-5 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[13px] font-semibold transition-colors items-center gap-2"
      >
        Browse coaches <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  );
}

function SkeletonList() {
  return (
    <div data-testid="my-sessions-loading" className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-2xl border border-slate-100 p-5 flex gap-4">
          <div className="w-12 h-12 rounded-full bg-slate-100" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-slate-100 rounded w-1/3" />
            <div className="h-3 bg-slate-100 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
